import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicApi } from '@/services/api';
import { useQuoteCart } from '@/context/QuoteCartContext';
import { ArrowLeft, Plus, Minus, ShoppingCart, Search, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { resolveImage, PRODUCT_IMAGE_BASE } from '@/lib/imageUrl';

const ProductDetailV2 = () => {
  const { seoUrl } = useParams();
  const [product, setProduct] = useState(null);
  const [variations, setVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { addItem } = useQuoteCart();

  useEffect(() => {
    loadProduct();
  }, [seoUrl]);

  const loadProduct = async () => {
    try {
      const [prodRes, varRes] = await Promise.all([
        publicApi.getProduct(seoUrl),
        publicApi.getProductVariations(seoUrl)
      ]);
      setProduct(prodRes.data);
      setVariations(varRes.data);
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVariations = useMemo(() => {
    if (!searchTerm) return variations;
    const term = searchTerm.toLowerCase();
    return variations.filter(v => 
      v.codigo.toLowerCase().includes(term) || 
      v.descripcion.toLowerCase().includes(term)
    );
  }, [variations, searchTerm]);

  const handleAddToCart = () => {
    if (variations.length > 0 && !selectedVariation) {
      toast.error('Selecciona una variación/medida del producto');
      return;
    }
    if (variations.length === 0 && !product.codigo) {
      toast.error('Este producto no tiene un código asignado. No se puede agregar al cotizador.');
      return;
    }
    if (quantity < 1) {
      toast.error('La cantidad debe ser al menos 1');
      return;
    }

    const cartItem = {
      product_id: product.id,
      nombre: product.nombre,
      ruta_img: product.ruta_img,
      codigo: selectedVariation ? selectedVariation.codigo : product.codigo,
      variacion: selectedVariation ? selectedVariation.descripcion : '',
      cantidad: quantity,
    };
    addItem(cartItem);
    toast.success(
      selectedVariation 
        ? `${selectedVariation.codigo} - ${selectedVariation.descripcion} agregado al cotizador`
        : `${product.codigo} - ${product.nombre} agregado al cotizador`
    );
    setSelectedVariation(null);
    setQuantity(1);
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="bg-v2-darker min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-v2-orange border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-v2-darker min-h-screen flex flex-col items-center justify-center text-white">
        <h2 className="text-2xl font-bold mb-4">Producto no encontrado</h2>
        <Link to="/productos" className="bg-v2-orange text-white px-8 py-4 inline-block font-bold uppercase tracking-wider">
          Ver Catálogo
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-v2-darker min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-v2-dark border-b border-v2-border">
        <div className="max-w-7xl mx-auto px-8 py-4">
          <div className="flex items-center gap-2 text-sm text-v2-text-muted">
            <Link to="/" className="hover:text-v2-orange transition-colors">Inicio</Link>
            <span>/</span>
            <Link to="/productos" className="hover:text-v2-orange transition-colors">Productos</Link>
            <span>/</span>
            <span className="text-v2-orange">{product.nombre}</span>
          </div>
        </div>
      </div>

      {/* Back button */}
      <div className="max-w-7xl mx-auto px-8 pt-6">
        <Link 
          to="/productos" 
          className="inline-flex items-center gap-2 text-v2-text-muted hover:text-v2-orange transition-colors"
        >
          <ArrowLeft size={16} /> Volver al catálogo
        </Link>
      </div>

      {/* Product Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Image */}
          <div className="min-w-0 bg-v2-gray border border-v2-border overflow-hidden">
            <img
              src={resolveImage(product.ruta_img, PRODUCT_IMAGE_BASE)}
              alt={product.nombre}
              className="w-full max-w-full h-72 sm:h-[400px] lg:h-[500px] object-cover"
              onError={(e) => { e.target.src = 'https://placehold.co/600x500/1A1A1A/FF6B00?text=Producto'; }}
            />
          </div>

          {/* Details */}
          <div className="min-w-0">
            {product.categoria && (
              <div className="flex items-center gap-2 mb-4">
                <span className="w-8 h-0.5 bg-v2-orange" />
                <span className="text-v2-orange uppercase text-sm tracking-wider font-medium">{product.categoria}</span>
              </div>
            )}
            
            <h1 className="text-4xl font-bold text-white mb-4" data-testid="product-name">{product.nombre}</h1>
            
            <p className="text-v2-text-muted leading-relaxed text-lg mb-8" data-testid="product-description">
              {product.descripcion}
            </p>

            {/* Variation Selector */}
            {variations.length > 0 && (
              <div className="mb-6" data-testid="variation-selector">
                <label className="block text-white font-medium mb-2">
                  Selecciona un producto *
                </label>
                <div className="relative">
                  <div 
                    className={`w-full px-4 py-3 bg-v2-dark border rounded cursor-pointer flex items-center justify-between text-white ${dropdownOpen ? 'border-v2-orange' : 'border-v2-border'}`}
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    data-testid="variation-dropdown-trigger"
                  >
                    <span className={selectedVariation ? 'text-white' : 'text-v2-text-muted'}>
                      {selectedVariation 
                        ? `${selectedVariation.codigo} - ${selectedVariation.descripcion}`
                        : 'Selecciona una variación...'
                      }
                    </span>
                    <ChevronDown size={18} className={`text-v2-text-muted transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
                  </div>

                  {dropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-v2-dark border border-v2-orange rounded shadow-2xl max-h-64 overflow-hidden" data-testid="variation-dropdown-list">
                      {/* Search */}
                      <div className="p-3 border-b border-v2-border sticky top-0 bg-v2-dark">
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-v2-text-muted" />
                          <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar por código o descripción..."
                            className="w-full pl-9 pr-4 py-2 bg-v2-gray border border-v2-border rounded text-white text-sm placeholder-v2-text-muted focus:border-v2-orange focus:outline-none"
                            data-testid="variation-search"
                            autoFocus
                          />
                        </div>
                      </div>
                      {/* Options */}
                      <div className="overflow-y-auto max-h-48">
                        {filteredVariations.length === 0 ? (
                          <div className="px-4 py-3 text-v2-text-muted text-sm text-center">No se encontraron variaciones</div>
                        ) : (
                          filteredVariations.map(v => (
                            <div
                              key={v.id}
                              className={`px-4 py-3 cursor-pointer hover:bg-v2-orange/20 flex items-center justify-between transition-colors ${selectedVariation?.id === v.id ? 'bg-v2-orange/10 border-l-2 border-v2-orange' : ''}`}
                              onClick={() => {
                                setSelectedVariation(v);
                                setDropdownOpen(false);
                                setSearchTerm('');
                              }}
                              data-testid={`variation-option-${v.id}`}
                            >
                              <div>
                                <span className="text-v2-orange font-mono text-sm font-bold">{v.codigo}</span>
                                <span className="text-white ml-3">{v.descripcion}</span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {variations.length === 0 && product.codigo && (
              <div className="mb-6 p-4 bg-v2-gray border border-v2-border rounded" data-testid="product-single-code">
                <div className="flex items-center gap-2">
                  <span className="text-v2-orange font-mono font-bold bg-v2-orange/10 px-3 py-1 rounded text-sm">{product.codigo}</span>
                  <span className="text-white text-sm">Producto unico</span>
                </div>
              </div>
            )}

            {variations.length === 0 && !product.codigo && (
              <div className="mb-6 p-4 bg-red-900/20 border border-red-700/40 rounded" data-testid="product-no-code-warning">
                <p className="text-red-400 text-sm font-medium">Este producto no tiene codigo ni variaciones registradas. No se puede agregar al cotizador.</p>
              </div>
            )}

            {/* Quantity + Add to Cart */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-8">
              <div className="flex items-center border border-v2-border rounded">
                <button 
                  onClick={() => setQuantity(Math.max(1, quantity - 1))} 
                  className="px-4 py-3 text-v2-text-muted hover:text-white hover:bg-v2-light-gray transition-colors"
                  data-testid="qty-minus"
                >
                  <Minus size={18} />
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-16 text-center bg-transparent text-white border-x border-v2-border py-3 focus:outline-none"
                  data-testid="qty-input"
                />
                <button 
                  onClick={() => setQuantity(quantity + 1)} 
                  className="px-4 py-3 text-v2-text-muted hover:text-white hover:bg-v2-light-gray transition-colors"
                  data-testid="qty-plus"
                >
                  <Plus size={18} />
                </button>
              </div>

              {(variations.length > 0 || product.codigo) ? (
                <button
                  onClick={handleAddToCart}
                  className="group w-full sm:flex-1 flex items-center justify-center gap-3 bg-v2-orange hover:bg-v2-orange-dark text-white font-bold uppercase tracking-wider text-sm sm:text-base px-4 sm:px-8 py-4 transition-all duration-300"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart size={20} />
                  Agregar al Cotizador
                </button>
              ) : (
                <button
                  disabled
                  className="group w-full sm:flex-1 flex items-center justify-center gap-3 bg-gray-600 text-gray-400 font-bold uppercase tracking-wider text-sm sm:text-base px-4 sm:px-8 py-4 cursor-not-allowed"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 10px), calc(100% - 10px) 100%, 0 100%)' }}
                  data-testid="add-to-cart-btn-disabled"
                >
                  <ShoppingCart size={20} />
                  No disponible para cotizar
                </button>
              )}
            </div>

            {/* Info Tags */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-v2-gray border border-v2-border p-4 rounded">
                <span className="text-v2-orange text-sm font-bold uppercase">Categoría</span>
                <p className="text-white mt-1">{product.categoria || 'General'}</p>
              </div>
              <div className="bg-v2-gray border border-v2-border p-4 rounded">
                <span className="text-v2-orange text-sm font-bold uppercase">
                  {variations.length > 0 ? 'Variaciones' : 'Código'}
                </span>
                <p className="text-white mt-1">
                  {variations.length > 0 
                    ? `${variations.length} disponibles` 
                    : (product.codigo || 'Sin asignar')
                  }
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailV2;
